import { isEmpty } from 'lodash';
import { Controller, useForm } from 'react-hook-form';
import type { ModalProps } from 'src/components/modal/Modal.interface';
import { Snackbar } from 'src/components/snackbar';
import { useCreateProductVariationFlavor } from 'src/hooks';
import { lang } from 'src/utils';

import { SimpleDialog } from '../../modal';
import { InputWidget } from '../../widget';

interface CreateVariationFlavorModalProps
  extends Omit<ModalProps, 'children'> {}
interface FormValues {
  name: string;
}

export const CreateVariationFlavorModal = (
  props: CreateVariationFlavorModalProps & { onClose: () => void },
) => {
  const createProductVariationFlavor = useCreateProductVariationFlavor();

  const form = useForm({
    defaultValues: { name: '' },
    mode: 'onChange',
  });

  const isNameEmpty = isEmpty(form.watch('name'));

  const handleSubmitData = async (data: FormValues) => {
    try {
      await createProductVariationFlavor.mutateAsync(data);

      Snackbar.success(`${data.name} ${lang.successfullyAdded}`);

      props.onClose();
    } catch (error: any) {
      switch (error?.response?.data?.error) {
        case lang.errors.duplicateItem: {
          form.setError('name', {
            message: lang.errors.duplicatedName,
          });
          break;
        }
        default: {
          Snackbar.error(error?.response?.data?.error ?? error?.message);
          break;
        }
      }
    }
  };

  return (
    <>
      <SimpleDialog
        {...props}
        modalSize="sm"
        title={lang.createProductVariationFlavorModal.title}
        confirmText={lang.confirm}
        disabled={!isEmpty(form.formState.errors) || isNameEmpty}
        isLoading={createProductVariationFlavor.isLoading}
        onClickCancel={props.onClose}
        onClickConfirm={form.handleSubmit(handleSubmitData)}>
        <Controller
          name="name"
          control={form.control}
          rules={{
            required: '',
            maxLength: {
              value: 100,
              message: lang.errors.maxLength(100),
            },
          }}
          render={({ field, fieldState }) => {
            return (
              <InputWidget
                {...field}
                label={lang.flavorName}
                hasError={!!fieldState.error?.message}
                error={fieldState.error?.message}
              />
            );
          }}
        />
      </SimpleDialog>
    </>
  );
};
